import type { ReactNode } from "react";

interface HighlightRange {
  readonly start: number;
  readonly end: number;
}

/**
 * A user message's text with its STRUCTURED mentions highlighted: the
 * `@Player AI` / `[Player AI]` tokens and the `@Name` tokens selected from the
 * mention picker. Only the occurrences the server validated are marked, so a
 * typed lookalike stays plain text. Moved out of MessageBubble.tsx (Task 6) to
 * keep that file under the 200-line soft cap.
 */
export function StructuredUserText({
  text,
  highlightedOccurrences,
  personMentions = [],
}: {
  text: string;
  /** Selected ordinals across `@Player AI` and `[Player AI]` tokens. */
  highlightedOccurrences: readonly number[];
  personMentions?: ReadonlyArray<{
    readonly token: string;
    readonly occurrenceIndices: readonly number[];
  }>;
}) {
  const ranges = highlightRanges(text, highlightedOccurrences, personMentions);
  if (ranges.length === 0) return text;
  const parts: ReactNode[] = [];
  let cursor = 0;
  for (const range of ranges) {
    if (range.start > cursor) parts.push(text.slice(cursor, range.start));
    parts.push(
      // DS keep (PR 3): an inline text highlight inside a message sentence,
      // not a badge or chip.
      <span
        key={`${range.start}-${range.end}`}
        className="rounded bg-pb-cyan-soft px-0.5 font-medium text-pb-cyan-text"
      >
        {text.slice(range.start, range.end)}
      </span>,
    );
    cursor = range.end;
  }
  if (cursor < text.length) parts.push(text.slice(cursor));
  return <>{parts}</>;
}

/** Selected occurrences of every structured token, ordered and non-overlapping. */
export function highlightRanges(
  text: string,
  playerAIOccurrences: readonly number[],
  personMentions: ReadonlyArray<{
    readonly token: string;
    readonly occurrenceIndices: readonly number[];
  }>,
): HighlightRange[] {
  const ranges: HighlightRange[] = [];
  const selectedPlayerAI = new Set(playerAIOccurrences);
  let ordinal = 0;
  for (const occurrence of text.matchAll(/@Player AI|\[Player AI\]/g)) {
    if (selectedPlayerAI.has(ordinal)) {
      ranges.push({
        start: occurrence.index,
        end: occurrence.index + occurrence[0].length,
      });
    }
    ordinal += 1;
  }
  for (const mention of personMentions) {
    if (!mention.token) continue;
    const selected = new Set(mention.occurrenceIndices);
    let index = text.indexOf(mention.token);
    let personOrdinal = 0;
    while (index >= 0) {
      if (selected.has(personOrdinal)) {
        ranges.push({ start: index, end: index + mention.token.length });
      }
      personOrdinal += 1;
      index = text.indexOf(mention.token, index + mention.token.length);
    }
  }
  ranges.sort((left, right) => left.start - right.start);
  const ordered: HighlightRange[] = [];
  for (const range of ranges) {
    const previous = ordered.at(-1);
    if (previous && range.start < previous.end) continue;
    ordered.push(range);
  }
  return ordered;
}
